import { zodResolver } from "@hookform/resolvers/zod";
import { Textarea } from "@material-tailwind/react";
import Label from "components/Label/Label";
import Loading from "components/Loader/Loading";
import { AuthContext } from "context/appContext";
import { AnimatePresence, motion } from "framer-motion";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { useContext, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { FaExclamationTriangle, FaImage, FaTimes } from "react-icons/fa";
import {
  fetchTerminatedReservationByClient,
  sendReservationFeedback,
} from "redux/features/reservations/reservationSlice";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";
import Select from "shared/Select/Select";
import { socket } from "utils/socket";
import { z } from "zod";
import CommonLayout from "./CommonLayout";
import { useNavigate } from "react-router-dom";

const formSchema = z.object({
  sujet: z
    .string({ required_error: "Le sujet est obligatoire." })
    .min(1, { message: "Le sujet est obligatoire." }),
  reference: z.string({ required_error: "La réference est obligatoire." }),
  description: z
    .string({ required_error: "La description est obligatoire." })
    .min(10, { message: "La description doit contenir au moins 10 caractères" }),
  images: z
    .instanceof(File)
    .optional()
    .refine(
      (file) => !file || file.size <= 5 * 1024 * 1024, // Max 5MB
      { message: "L'image doit être inférieure à 5 Mo." }
    ),
});

const ClientReclamationPage = () => {
  const authContext = useContext(AuthContext);
  const profilId = authContext?.user?.profil?.id;
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const reservations = useAppSelector((state) => state.featureReservation.reservations);
  const [referenceData, setReferenceData] = useState<any>(null);
  const [ownerId, setOwnerId] = useState<number | null>(null);

  useEffect(() => {
    if (profilId) {
      dispatch(fetchTerminatedReservationByClient(profilId)).then(() => setIsLoading(false));
    }
  }, [profilId, dispatch]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    trigger,
    reset,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {},
    mode: "onSubmit",
  });
  const navigate = useNavigate();
  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = async (data) => {
    if (!ownerId) return;

    const formData = { ...data, ownerId };
    const res = await dispatch(sendReservationFeedback(formData));
    if (res.payload.status === "OK") {
      socket.emit("send-notification", {
        senderId: referenceData?.Client?.userId,
        receiverId: referenceData?.Owner?.userId,
        action: "send_reservation_feedback",
        reservationId: referenceData?.id,
        reservation: referenceData?.reference,
        location: referenceData?.Location?.title,
        locationId: referenceData?.Location?.id,
      });
    }
    navigate("/client/reclamations-reservations");
    setIsLoading(false);
    reset();
    setOwnerId(null);
    setReferenceData(null);
    setImagePreview(null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setValue("images", file);
      trigger("images");
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setValue("images", undefined);
      setImagePreview(null);
    }
  };

  const handleImageDelete = () => {
    setValue("images", undefined);
    setImagePreview(null);
    trigger("images");
  };

  const renderSection1 = () => {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="container px-4 py-8 mx-auto"
      >
        <div className="flex items-center mb-8 space-x-3">
          <FaExclamationTriangle className="text-3xl text-blue-500 dark:text-blue-400" aria-hidden="true" />
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            Envoyer une Réclamation
          </h2>
        </div>

        <motion.form
          onSubmit={handleSubmit(onSubmit)}
          className="p-6 space-y-6 bg-white shadow-lg dark:bg-gray-800 rounded-xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <div className="mb-6 border-b w-14 border-neutral-200 dark:border-neutral-700"></div>

          <div className="relative">
            <Label>
              Sujet <span className="text-red-500">*</span>
            </Label>
            <motion.div>
              <Input
                type="text"
                placeholder="Entrez le sujet..."
                className={`mt-1 transition-all duration-300 ease-in-out ${
                  errors.sujet?.message ? "border-red-500" : "border-gray-200 dark:border-gray-700"
                } focus:ring-2 focus:ring-blue-500 focus:border-blue-500  `}
                disabled={isLoading}
                {...register("sujet")}
              />
            </motion.div>
            <AnimatePresence>
              {errors.sujet?.message && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-1 text-xs text-red-500"
                >
                  {errors.sujet?.message}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="relative">
            <Label>
              Réservation <span className="text-red-500">*</span>{" "}
            </Label>
            <motion.div>
              <Select
                style={{ minWidth: "306px" }}
                className={`transition-all duration-300 ease-in-out ${
                  errors.reference?.message ? "border-red-500" : "border-gray-200 dark:border-gray-700"
                } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                disabled={isLoading}
                value={getValues("reference")}
                onChange={(e) => {
                  const selectedReference = e.target.value;
                  setValue("reference", selectedReference);
                  const selectedReservation: any = reservations.find(
                    (reservation: any) => reservation.reference === selectedReference
                  );
                  if (selectedReservation) {
                    setReferenceData(selectedReservation);
                    setOwnerId(selectedReservation?.ownerId);
                  } else {
                    setOwnerId(null);
                    setReferenceData(null);
                  }
                  trigger("reference");
                }}
              >
                <option value="">Choisir une référence</option>
                {reservations?.map((ref: any) => (
                  <option key={ref.reference} value={ref.reference}>
                    {`${ref.reference} - ${ref.Location?.title}`}
                  </option>
                ))}
              </Select>
            </motion.div>
            <AnimatePresence>
              {errors.reference?.message && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-1 text-xs text-red-500"
                >
                  {errors.reference?.message}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div>
            <Label>
              Description <span className="text-red-500">*</span>
            </Label>
            <motion.div>
              <Textarea
                placeholder="Décrivez votre réclamation..."
                rows={14}
                disabled={isLoading}
                {...register("description")}
                className={`transition-all duration-300 ease-in-out ${
                  errors.description?.message ? "border-red-500" : "border-gray-200 dark:border-gray-700"
                } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
            </motion.div>
            <AnimatePresence>
              {errors.description?.message && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-1 text-xs text-red-500"
                >
                  {errors.description?.message}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div>
            <Label>Image</Label>
            <motion.div whileHover={{ scale: 1.01 }} transition={{ duration: 0.2 }}>
              <div className="relative flex items-center justify-center w-full">
                <label
                  htmlFor="image-upload"
                  className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-all duration-300 ease-in-out ${
                    errors.images?.message
                      ? "border-red-500"
                      : "border-gray-200 dark:border-gray-700 hover:border-blue-500"
                  } bg-gray-50 dark:bg-gray-800`}
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <FaImage className="w-8 h-8 mb-2 text-gray-400 dark:text-gray-500" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Cliquez pour télécharger une image (max 5 Mo)
                    </p>
                  </div>
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={isLoading}
                    onChange={handleImageChange}
                  />
                </label>
              </div>
            </motion.div>
            <AnimatePresence>
              {imagePreview && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="relative mt-4"
                >
                  <img
                    src={imagePreview}
                    alt="Image preview"
                    className="object-cover w-full max-w-xs rounded-lg shadow-md"
                  />
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={handleImageDelete}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 shadow-md hover:bg-red-600 transition-colors"
                    aria-label="Supprimer l'image"
                  >
                    <FaTimes className="w-4 h-4" />
                  </motion.button>
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {errors.images?.message && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-1 text-xs text-red-500"
                >
                  {errors.images?.message}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center justify-center"
          >
            <ButtonPrimary
              type="submit"
              loading={isLoading}
              disabled={isLoading}
              className="w-full max-w-xs bg-eventToBee"
            >
              Envoyer
            </ButtonPrimary>
          </motion.div>
        </motion.form>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <CommonLayout>
        {isLoading && <Loading />}
        {!isLoading && renderSection1()}
      </CommonLayout>
    </div>
  );
};

export default ClientReclamationPage;
